import { isEmpty } from 'lodash';
import { dataIsColors } from 'utils/typeguards/predictionOutputs';

export const hasRegions = (checkObj: CF.API.TOutputData): checkObj is CF.API.BaseRegionsData => {
  const outputData = checkObj as CF.API.BaseRegionsData;
  const isObject = outputData !== null && typeof outputData === 'object';
  return isObject && outputData.regions !== undefined;
};

export const hasRegionsWithConcepts = (checkObj: CF.API.TOutputData): checkObj is CF.API.BaseRegionsData => {
  const outputData = checkObj as CF.API.BaseRegionsData;
  const isObject = outputData !== null && typeof outputData === 'object';
  const hasRegionsField = isObject && !isEmpty(outputData.regions);
  const hasConceptsField =
    isObject && !!outputData.regions && !isEmpty(outputData.regions) && outputData.regions.some((region) => region.data.concepts !== undefined);
  return hasRegionsField && hasConceptsField;
};

export const hasFramesWithRegions = (checkObj: CF.API.TOutputData): checkObj is CF.API.BaseFramesData => {
  const outputData = checkObj as CF.API.BaseFramesData;
  const isObject = outputData !== null && typeof outputData === 'object';
  const hasFramesField = isObject && outputData?.frames !== undefined;
  const frames = outputData?.frames ? outputData.frames : [];
  const regionsData = frames.length ? frames.some((frame) => !!(frame.data as CF.API.BaseRegionsData).regions) : false;
  return regionsData && hasFramesField;
};

export const hasConcepts = (checkObj: CF.API.TOutputData): checkObj is CF.API.BaseConceptsData => {
  const outputData = checkObj as CF.API.BaseConceptsData;
  const isObject = outputData !== null && typeof outputData === 'object';
  return isObject && outputData.concepts !== undefined;
};

export const hasText = (checkObj: CF.API.TOutputData): checkObj is CF.API.BaseTextData => {
  const outputData = checkObj as CF.API.BaseTextData;
  const isObject = outputData !== null && typeof outputData === 'object';
  return isObject && outputData.text !== undefined;
};

export const hasImages = (checkObj: CF.API.TOutputData): checkObj is CF.API.BaseImageData => {
  const outputData = checkObj as CF.API.BaseImageData;
  const isObject = outputData !== null && typeof outputData === 'object';
  return isObject && outputData.image !== undefined;
};

export const hasBbox = (checkObj: CF.API.RegionInfo): checkObj is CF.API.BboxRegionInfo => {
  const outputData = checkObj as CF.API.BboxRegionInfo;
  const isObject = outputData !== null && typeof outputData === 'object';
  return isObject && outputData.bounding_box !== undefined;
};

export const hasPolygon = (checkObj: CF.API.RegionInfo): checkObj is CF.API.PolygonRegionInfo => {
  const outputData = checkObj as CF.API.PolygonRegionInfo;
  const isObject = outputData !== null && typeof outputData === 'object';
  return isObject && outputData.polygon !== undefined;
};

export const hasMask = (checkObj: CF.API.RegionInfo): checkObj is CF.API.MaskShapeProps => {
  const outputData = checkObj as CF.API.MaskShapeProps;
  const isObject = outputData !== null && typeof outputData === 'object';
  return isObject && outputData.mask !== undefined;
};

export const hasFrames = (checkObj: CF.API.TOutputData): checkObj is CF.API.BaseFramesData => {
  const outputData = checkObj as CF.API.BaseFramesData;
  const isObject = outputData !== null && typeof outputData === 'object';
  return isObject && outputData.frames !== undefined;
};

export const hasAudio = (checkObj: CF.API.TOutputData): boolean => {
  const outputData = checkObj as unknown as { audio?: { base64?: string } };
  const isObject = outputData !== null && typeof outputData === 'object';
  const hasBase64Field = isObject && typeof outputData.audio === 'object' && outputData.audio !== null && typeof outputData.audio.base64 === 'string';
  return hasBase64Field;
};

export const getFormattedValue = (score: number): string => {
  if (score === 1) return score.toFixed(1);
  else {
    const value = Number.parseFloat(score.toFixed(8).toString().slice(0, 5));
    return value === 0 ? '0.000' : value.toString();
  }
};

export const hasColors = (checkObj: CF.API.TOutputData): checkObj is CF.API.BaseColorsData => {
  return dataIsColors(checkObj);
};

export const transformRegionData = (
  regions: CF.API.RegionsEntity[],
): Record<string, Array<{ id: string; regionInfo: CF.API.BboxRegionInfo | CF.API.MaskShapeProps; conceptValue: string }>> => {
  return regions.reduce((acc, region) => {
    const { id, data: regionData, region_info } = region;

    if (!regionData.concepts || !(hasBbox(region_info) || hasMask(region_info))) {
      return acc;
    }
    // display only concept with highest score
    const { name: conceptName, value } = regionData.concepts[0];

    const conceptValue = getFormattedValue(value || 0);

    if (conceptName in acc) {
      acc[conceptName].push({ id, regionInfo: region_info, conceptValue });
    } else {
      acc[conceptName] = [{ id, regionInfo: region_info, conceptValue }];
    }
    return acc;
  }, {} as Record<string, Array<{ id: string; regionInfo: CF.API.BboxRegionInfo | CF.API.MaskShapeProps; conceptValue: string }>>);
};
